import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, AlertCircle, Mail, User, Calendar } from 'lucide-react';
import { unsubscribeAPI } from '../services/api';

const Unsubscribe = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [leadData, setLeadData] = useState(null);

  useEffect(() => {
    handleUnsubscribe();
  }, [token]);

  const handleUnsubscribe = async () => {
    try {
      setLoading(true);
      const response = await unsubscribeAPI.unsubscribe(token);
      
      if (response.data) {
        setSuccess(true);
        setLeadData(response.data.lead);
      } else {
        setError('An unexpected error has occurred');
      }
    } catch (err) {
      const errorMessage = err.response?.data?.error || 'Connection error occurred';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <span className="ml-3 text-gray-600">Processing...</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Error</h2>
          <p className="text-gray-600 mb-6">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full">
        {success && leadData && (
          <>
            <div className="text-center mb-6">
              <CheckCircle className="h-12 w-12 text-green-500 mx-auto mb-4" />
              
              <h2 className="text-xl font-semibold text-gray-800 mb-2">
              You have unsubscribed from the email newsletter.
              </h2>
              
              <p className="text-gray-600">
              You have successfully unsubscribed from the email newsletter. You will no longer receive emails.
              </p>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
              <div className="flex items-center mb-3">
                <User className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-sm font-medium text-gray-700">Personal Information</span>
              </div>
              <p className="text-gray-800 font-medium">{leadData.fullName}</p>
              <div className="flex items-center mt-2">
                <Mail className="h-4 w-4 text-gray-500 mr-2" />
                <span className="text-gray-600">{leadData.email}</span>
              </div>
              
              {leadData.unsubscribeDate && (
                <div className="flex items-center mt-2">
                  <Calendar className="h-4 w-4 text-gray-500 mr-2" />
                  <span className="text-gray-600 text-sm">
                    Unsubscribe date: {new Date(leadData.unsubscribeDate).toLocaleDateString('tr-TR')}
                  </span>
                </div>
              )}
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500 mb-4">
                If you have performed this action by mistake, please contact us.
              </p>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe; 